import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, checkAuth, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/tenants/public").then(({ data }) => setTenants(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && user === null) navigate("/login", { replace: true });
    if (user && (user.tenant_id || user.role === "super_admin")) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const submit = async () => {
    if (!tenantId) return toast.error("Select an organization");
    setBusy(true);
    try {
      await api.post("/auth/onboard", { tenant_id: tenantId });
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-sm bg-card border border-border rounded-md p-8">
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Join an organization</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Welcome{user?.name ? `, ${user.name}` : ""}. Select your organization to continue.
        </p>
        <div className="mt-6 space-y-4">
          <Select value={tenantId} onValueChange={setTenantId}>
            <SelectTrigger data-testid="onboard-tenant" className="rounded-sm"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent>
              {tenants.map((t) => (
                <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={submit} disabled={busy} data-testid="onboard-submit" className="w-full rounded-sm">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Continue
          </Button>
          <Button variant="ghost" onClick={logout} className="w-full rounded-sm" data-testid="onboard-logout">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
